import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { DATA_SYNCING_WEBSOCKET_STATUS, LOCAL_STORAGE_KEYS } from "../shared/constants";

interface DataSyncWebSocketProps {
  accessToken?: string;
  // Optional callback on task complete
  onComplete?: () => void;
  // Optional callback on task failure
  onError?: (error?: unknown) => void;
}

type ProgressPayload = {
  progress?: number;
  status?: string;
  message?: string;
  task_id?: string | number;
  total_tasks?: number;
  completed_tasks?: number;
  timestamp?: string | number;
  stage?: string;
  completed?: boolean;
  error?: string | Record<string, unknown> | null;
};

export const useDataSyncingWebsocket = ({
  accessToken,
  onComplete,
  onError,
}: DataSyncWebSocketProps) => {
  const wsUrl = import.meta.env.VITE_WEB_SOCKET_URL;

  const [wsConnected, setWsConnected] = useState(false);
  const [progress, setProgress] = useState<number>(0);
  const [status, setStatus] = useState<string>("idle");
  const [message, setMessage] = useState<string>("");
  const [isCompleted, setIsCompleted] = useState<boolean>(false);
  const [isError, setIsError] = useState<boolean>(false);
  const [lastEvent, setLastEvent] = useState<ProgressPayload | null>(null);
  const [completedTasks, setCompletedTasks] = useState<number>(0);
  const [totalTasks, setTotalTasks] = useState<number>(0);
  const [taskId, setTaskId] = useState<string>("");

  const wsRef = useRef<WebSocket | null>(null);
  const currentKeyRef = useRef<string | null>(null);
  const isOpenRef = useRef<boolean>(false);

  const connectionKey = useMemo(() => {
    if (!wsUrl || !accessToken) return null;
    return `${wsUrl}|${accessToken}|${taskId ?? ""}`;
  }, [wsUrl, accessToken, taskId]);

  const close = useCallback(() => {
    const ws = wsRef.current;
    if (!ws) return;
    try {
      console.log("[DataSyncWS] Manual close() invoked", {
        readyState: ws.readyState,
        isOpen: isOpenRef.current,
      });
      ws.close();
    } catch {
      console.log("Manual close() invoked");
    }
    wsRef.current = null;
    isOpenRef.current = false;
  }, []);

  useEffect(() => {
    // Require wsUrl to establish the connection
    if (!wsUrl || !accessToken) {
      try {
        console.warn("[DataSyncWS] Missing wsUrl or accessToken; skipping connection", {
          hasWsUrl: Boolean(wsUrl),
          hasAccessToken: Boolean(accessToken),
        });
      } catch {
        console.log("Missing wsUrl or accessToken; skipping connection");
      }
      return;
    }

    // Avoid duplicating connection for the same key
    if (
      currentKeyRef.current === connectionKey &&
      wsRef.current &&
      (wsRef.current.readyState === WebSocket.CONNECTING ||
        wsRef.current.readyState === WebSocket.OPEN)
    ) {
      try {
        console.log("[DataSyncWS] Reusing existing socket; preventing duplicate connection", {
          connectionKey,
          readyState: wsRef.current?.readyState,
        });
      } catch {
        console.log("Reusing existing socket; preventing duplicate connection");
      }
      return;
    }

    // Close existing connection if key changed
    if (wsRef.current && currentKeyRef.current !== connectionKey) {
      try {
        console.log("[DataSyncWS] Closing existing socket due to connectionKey change", {
          previousKey: currentKeyRef.current,
          nextKey: connectionKey,
          prevReadyState: wsRef.current.readyState,
        });
        wsRef.current.close();
      } catch {
        console.log("Closing existing socket due to connectionKey change");
      }
      wsRef.current = null;
      isOpenRef.current = false;
    }

    currentKeyRef.current = connectionKey;

    // Require taskId to subscribe to task progress
    if (!taskId) {
      try {
        console.log("[DataSyncWS] No taskId provided; skipping connection");
      } catch {
        console.log("Skipping connection");
      }
      return;
    }

    const fullUrl = `${wsUrl}/task-progress/?token=${accessToken}&task_id=${taskId}`;
    try {
      console.log("[DataSyncWS] Creating WebSocket", { fullUrl, connectionKey });
    } catch {
      console.log("Creating WebSocket");
    }

    const ws = new WebSocket(fullUrl);
    wsRef.current = ws;

    ws.onopen = () => {
      isOpenRef.current = true;
      setWsConnected(true);
      // reset state when (re)connecting
      setIsCompleted(false);
      setIsError(false);
      setStatus("in_progress");
      try {
        console.log("[DataSyncWS] Socket opened", {
          url: (ws)?.url,
          readyState: ws.readyState,
        });
      } catch {
        console.log("Socket opened");
      }
    };

    ws.onclose = (evt) => {
      isOpenRef.current = false;
      setWsConnected(false);
      try {
        console.warn("[DataSyncWS] Socket closed", {
          code: evt.code,
          reason: evt.reason,
          wasClean: (evt as CloseEvent).wasClean,
        });
      } catch {
        console.log("Socket closed");
      }
      // Token invalid or origin not allowed — do not retry
      if (evt.code === 4001 || evt.code === 4003) {
        console.warn("[DataSyncWS] Auth/origin error, not retrying", evt.code);
      }
    };

    ws.onerror = (evt) => {
      // Better diagnostics on socket errors
      try {
        console.error("[DataSyncWS] Socket error", {
          event: evt,
          readyState: ws.readyState,
          url: (ws)?.url,
        });
      } catch {
        console.log("Socket error");
      }
      setIsError(true);
      if (onError) onError(evt);
    };

    ws.onmessage = (evt) => {
      try {
        const data: ProgressPayload = JSON.parse(evt.data || "{}");
        console.log("[DataSyncWS] Message received", data?.progress);
        setLastEvent(data);

        if (typeof data.progress === "number") setProgress(data.progress);
        if (typeof data.status === "string") setStatus(data.status);
        if (typeof data.message === "string") setMessage(data.message);
        if (typeof data.completed_tasks === "number") setCompletedTasks(data.completed_tasks);
        if (typeof data.total_tasks === "number") setTotalTasks(data.total_tasks);

        if (data.error) {
          setIsError(true);
          try {
            console.warn("[DataSyncWS] Error payload received", { error: data.error });
          } catch {
            console.log("Error payload received");
          }
          if (onError) onError(data.error);
        }

        const countsDone =
          typeof data.completed_tasks === "number" &&
          typeof data.total_tasks === "number" &&
          data.total_tasks > 0 &&
          data.completed_tasks >= data.total_tasks;

        if (data.status === DATA_SYNCING_WEBSOCKET_STATUS.COMPLETED) {
          setIsCompleted(true);
          try {
            console.log("[DataSyncWS] Completion detected", {
              countsDone,
              status: data.status,
            });
          } catch {
            console.log("Completion detected");
          }
          if (onComplete) onComplete();
        }
      } catch (e) {
        try {
          console.warn("[DataSyncWS] Failed to parse message payload", {
            raw: evt.data,
            error: e,
          });
        } catch {
          console.log("Failed to parse message payload");
        }
        // ignore invalid payloads
      }
    };

    return () => {
      try {
        console.log("[DataSyncWS] Effect cleanup: closing socket if open", {
          readyState: ws.readyState,
        });
        ws.close();
      } catch {
        console.log("Effect cleanup: closing socket if open");
      }
      wsRef.current = null;
      isOpenRef.current = false;
    };
  }, [wsUrl, accessToken, taskId, connectionKey, onError, onComplete]);

  useEffect(() => {
    const storedTaskId = localStorage.getItem(LOCAL_STORAGE_KEYS.TASK_ID)
    if (storedTaskId) {
      setTaskId(storedTaskId)
    }
  }, [])

  const reset = useCallback(() => {
    try {
      console.log("[DataSyncWS] reset() state to initial values", {
        prev: {
          progress,
          status,
          message,
          isCompleted,
          isError,
          completedTasks,
          totalTasks,
        },
      });
    } catch {
      console.log("reset() state to initial values");
    }
    setProgress(0);
    setStatus("idle");
    setMessage("");
    setIsCompleted(false);
    setIsError(false);
    setLastEvent(null);
    setCompletedTasks(0);
    setTotalTasks(0);
  }, [completedTasks, isCompleted, isError, message, progress, status, totalTasks]);

  return {
    wsConnected,
    progress,
    status,
    message,
    isCompleted,
    isError,
    lastEvent,
    completedTasks,
    totalTasks,
    close,
    reset,
    taskId,
    setTaskId
  } as const;
};
